import { useState } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';

const AppointmentPage = () => {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    dateOfBirth: '',
    department: '',
    preferredDate: '',
    preferredTime: '',
    appointmentType: '',
    insuranceProvider: '',
    reasonForVisit: '',
    urgency: '',
    previousPatient: ''
  });

  const [submitStatus, setSubmitStatus] = useState<{ type: 'success' | 'error'; message: string } | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitStatus(null);

    try {
      const formDataEncoded = new URLSearchParams();
      Object.entries(formData).forEach(([key, value]) => {
        formDataEncoded.append(key, String(value));
      });

      const response = await fetch('https://readdy.ai/api/form/d58v9ufahj0hkf71i8fg', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formDataEncoded.toString()
      });

      if (response.ok) {
        setSubmitStatus({ 
          type: 'success', 
          message: 'Appointment request submitted successfully! Our scheduling team will contact you within 24 hours to confirm your appointment.' 
        });
        setFormData({
          firstName: '',
          lastName: '',
          email: '',
          phone: '',
          dateOfBirth: '',
          department: '',
          preferredDate: '',
          preferredTime: '',
          appointmentType: '',
          insuranceProvider: '',
          reasonForVisit: '',
          urgency: '',
          previousPatient: ''
        });
      } else {
        setSubmitStatus({ type: 'error', message: 'Submission failed. Please try again or call us directly.' });
      }
    } catch (error) {
      setSubmitStatus({ type: 'error', message: 'Network error. Please try again or call us at (720) 655-2560.' });
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      {/* Hero Banner */}
      <section className="relative h-[40vh] flex items-center justify-center bg-gradient-to-r from-primary to-primary-dark">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxwYXRoIGQ9Ik0zNiAxOGMzLjMxNCAwIDYgMi42ODYgNiA2cy0yLjY4NiA2LTYgNi02LTIuNjg2LTYtNiAyLjY4Ni02IDYtNnoiIHN0cm9rZT0iI2ZmZiIgc3Ryb2tlLXdpZHRoPSIyIi8+PC9nPjwvc3ZnPg==')]"></div>
        </div>
        <div className="relative z-10 text-center px-6">
          <motion.h1
            className="text-5xl md:text-6xl font-bold text-white mb-4"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            Schedule Your Appointment
          </motion.h1>
          <motion.p
            className="text-white/80 text-lg"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.2 }}
          >
            <Link to="/" className="hover:text-white cursor-pointer">Home</Link> <span className="mx-2">/</span> Appointment
          </motion.p>
        </div>
      </section>

      {/* Quick Info */}
      <section className="container mx-auto px-6 -mt-8 relative z-20 mb-16">
        <div className="bg-white rounded-xl shadow-lg p-8">
          <div className="grid md:grid-cols-3 gap-8">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 bg-[rgba(75,162,75,0.1)] rounded-full flex items-center justify-center flex-shrink-0">
                <i className="ri-phone-line text-2xl text-[rgba(75,162,75)]"></i>
              </div>
              <div>
                <h3 className="font-bold text-gray-900">Call Us</h3>
                <a href="tel:+17206552560" className="text-[rgba(75,162,75)] hover:text-[rgba(75,162,75,0.8)] cursor-pointer">
                  (720) 655-2560
                </a>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 bg-[rgba(75,162,75,0.1)] rounded-full flex items-center justify-center flex-shrink-0">
                <i className="ri-time-line text-2xl text-[rgba(75,162,75)]"></i>
              </div>
              <div>
                <h3 className="font-bold text-gray-900">Office Hours</h3>
                <p className="text-gray-600 text-sm">Mon-Fri: 8AM-6PM</p>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 bg-[rgba(75,162,75,0.1)] rounded-full flex items-center justify-center flex-shrink-0">
                <i className="ri-calendar-check-line text-2xl text-[rgba(75,162,75)]"></i>
              </div>
              <div>
                <h3 className="font-bold text-gray-900">Same-Day Available</h3>
                <p className="text-gray-600 text-sm">For urgent needs</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Appointment Form */}
      <section className="container mx-auto px-6 pb-16">
        <div className="max-w-4xl mx-auto">
          <div className="bg-white rounded-xl shadow-lg p-10">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">Request an Appointment</h2>
            <p className="text-gray-600 mb-8">
              Complete this form and our scheduling team will contact you to confirm your appointment time.
            </p>

            {submitStatus && (
              <div className={`mb-6 p-4 rounded-lg ${submitStatus.type === 'success' ? 'bg-green-50 text-green-800 border border-green-200' : 'bg-red-50 text-red-800 border border-red-200'}`}>
                <div className="flex items-center gap-2">
                  <i className={`${submitStatus.type === 'success' ? 'ri-checkbox-circle-fill' : 'ri-error-warning-fill'} text-xl`}></i>
                  <span>{submitStatus.message}</span>
                </div>
              </div>
            )}

            <form id="appointment-form" data-readdy-form onSubmit={handleSubmit} className="space-y-6">
              {/* Personal Information */}
              <div>
                <h3 className="text-xl font-bold text-gray-900 mb-4">Personal Information</h3>
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">First Name *</label>
                    <input
                      type="text"
                      name="firstName"
                      required
                      value={formData.firstName}
                      onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[rgba(75,162,75)] focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Last Name *</label>
                    <input
                      type="text"
                      name="lastName"
                      required
                      value={formData.lastName}
                      onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[rgba(75,162,75)] focus:border-transparent"
                    />
                  </div>
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Email Address *</label>
                  <input
                    type="email"
                    name="email"
                    required
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[rgba(75,162,75)] focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Phone Number *</label>
                  <input
                    type="tel"
                    name="phone"
                    required
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[rgba(75,162,75)] focus:border-transparent"
                  />
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Date of Birth *</label>
                  <input
                    type="date"
                    name="dateOfBirth"
                    required
                    value={formData.dateOfBirth}
                    onChange={(e) => setFormData({ ...formData, dateOfBirth: e.target.value })}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[rgba(75,162,75)] focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Are you a previous patient? *</label>
                  <select
                    name="previousPatient"
                    required
                    value={formData.previousPatient}
                    onChange={(e) => setFormData({ ...formData, previousPatient: e.target.value })}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[rgba(75,162,75)] focus:border-transparent"
                  >
                    <option value="">Select</option>
                    <option value="Yes">Yes, I'm an existing patient</option>
                    <option value="No">No, I'm a new patient</option>
                  </select>
                </div>
              </div>

              {/* Appointment Details */}
              <div className="border-t border-gray-200 pt-6">
                <h3 className="text-xl font-bold text-gray-900 mb-4">Appointment Details</h3>
                
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Department/Service *</label>
                  <select
                    name="department"
                    required
                    value={formData.department}
                    onChange={(e) => setFormData({ ...formData, department: e.target.value })}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[rgba(75,162,75)] focus:border-transparent mb-6"
                  >
                    <option value="">Select Department</option>
                    <option value="Medication Management">Medication Management</option>
                    <option value="Psychiatric Evaluation">Psychiatric Evaluation</option>
                    <option value="Therapy Follow-ups">Therapy Follow-ups</option>
                    <option value="Dual Diagnosis Treatment">Dual Diagnosis Treatment</option>
                  </select>
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Preferred Date *</label>
                    <input
                      type="date"
                      name="preferredDate"
                      required
                      value={formData.preferredDate}
                      onChange={(e) => setFormData({ ...formData, preferredDate: e.target.value })}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[rgba(75,162,75)] focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Preferred Time *</label>
                    <select
                      name="preferredTime"
                      required
                      value={formData.preferredTime}
                      onChange={(e) => setFormData({ ...formData, preferredTime: e.target.value })}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[rgba(75,162,75)] focus:border-transparent"
                    >
                      <option value="">Select Time</option>
                      <option value="Morning (8AM-11AM)">Morning (8AM-11AM)</option>
                      <option value="Midday (11AM-2PM)">Midday (11AM-2PM)</option>
                      <option value="Afternoon (2PM-5PM)">Afternoon (2PM-5PM)</option>
                      <option value="Evening (5PM-6PM)">Evening (5PM-6PM)</option>
                    </select>
                  </div>
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Appointment Type *</label>
                  <select
                    name="appointmentType"
                    required
                    value={formData.appointmentType}
                    onChange={(e) => setFormData({ ...formData, appointmentType: e.target.value })}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[rgba(75,162,75)] focus:border-transparent"
                  >
                    <option value="">Select Type</option>
                    <option value="In-Office Visit">In-Office Visit</option>
                    <option value="Telepsychiatry (Video)">Telepsychiatry (Video)</option>
                    <option value="House Call">House Call</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Urgency Level *</label>
                  <select
                    name="urgency"
                    required
                    value={formData.urgency}
                    onChange={(e) => setFormData({ ...formData, urgency: e.target.value })}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[rgba(75,162,75)] focus:border-transparent"
                  >
                    <option value="">Select Urgency</option>
                    <option value="Routine">Routine (within 2 weeks)</option>
                    <option value="Moderate">Moderate (within 1 week)</option>
                    <option value="Urgent">Urgent (within 2-3 days)</option>
                    <option value="Crisis">Crisis (same day needed)</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Insurance Provider</label>
                <select
                  name="insuranceProvider"
                  value={formData.insuranceProvider}
                  onChange={(e) => setFormData({ ...formData, insuranceProvider: e.target.value })}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[rgba(75,162,75)] focus:border-transparent"
                >
                  <option value="">Select Insurance</option>
                  <option value="Optum">Optum</option>
                  <option value="Aetna">Aetna</option>
                  <option value="Cigna">Cigna</option>
                  <option value="Medicaid">Medicaid</option>
                  <option value="Medicare">Medicare</option>
                  <option value="Colorado Access">Colorado Access</option>
                  <option value="Self-Pay">Self-Pay</option>
                  <option value="Other">Other</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Reason for Visit *</label>
                <textarea
                  name="reasonForVisit"
                  required
                  rows={5}
                  maxLength={500}
                  value={formData.reasonForVisit}
                  onChange={(e) => setFormData({ ...formData, reasonForVisit: e.target.value })}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[rgba(75,162,75)] focus:border-transparent resize-none"
                  placeholder="Please briefly describe your concerns and what you hope to address in this appointment (max 500 characters)"
                ></textarea>
                <p className="text-sm text-gray-500 mt-1">{formData.reasonForVisit.length}/500 characters</p>
              </div>

              <div className="bg-[rgba(75,162,75,0.1)] rounded-lg p-6 border border-[rgba(75,162,75,0.3)]">
                <div className="flex items-start gap-3">
                  <i className="ri-information-line text-2xl text-[rgba(75,162,75)] mt-1"></i>
                  <div className="text-sm text-gray-700">
                    <p className="font-semibold mb-2">Important Information:</p>
                    <ul className="space-y-1">
                      <li>• This is an appointment request, not a confirmed booking</li>
                      <li>• Our team will contact you within 24 hours to confirm your appointment</li>
                      <li>• For urgent mental health crises, please call 911 or visit your nearest emergency room</li>
                      <li>• Crisis support: National Suicide Prevention Lifeline 988</li>
                    </ul>
                  </div>
                </div>
              </div>

              <button
                type="submit"
                className="w-full bg-[rgba(75,162,75)] text-white py-4 rounded-lg font-semibold hover:bg-[rgba(75,162,75,0.9)] transition-all shadow-lg whitespace-nowrap cursor-pointer"
              >
                <i className="ri-calendar-check-line mr-2"></i>
                Submit Appointment Request
              </button>
            </form>
          </div>

          {/* Contact Alternatives */}
          <div className="mt-8 grid md:grid-cols-2 gap-6">
            <div className="bg-white rounded-xl p-6 shadow-md border border-gray-100">
              <h3 className="text-lg font-bold text-gray-900 mb-3">Prefer to Call?</h3>
              <p className="text-gray-600 mb-4 text-sm">Speak directly with our scheduling team</p>
              <a
                href="tel:+17206552560"
                className="inline-flex items-center gap-2 text-[rgba(75,162,75)] hover:text-[rgba(75,162,75,0.8)] font-semibold cursor-pointer"
              >
                <i className="ri-phone-line"></i>
                (720) 655-2560
              </a>
            </div>

            <div className="bg-white rounded-xl p-6 shadow-md border border-gray-100">
              <h3 className="text-lg font-bold text-gray-900 mb-3">Email Us</h3>
              <p className="text-gray-600 mb-4 text-sm">Send your appointment request via email</p>
              <a
                href="mailto:appointments@solutionshealthcare.com"
                className="inline-flex items-center gap-2 text-[rgba(75,162,75)] hover:text-[rgba(75,162,75,0.8)] font-semibold cursor-pointer"
              >
                <i className="ri-mail-line"></i>
                appointments@solutionshealthcare.com
              </a>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default AppointmentPage;
